import { useEffect } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { fetchUser } from 'api/user';
import { userQueryKeys } from 'queries/user-query-keys';

type TransformedUser = ReturnType<typeof useCurrentUser>;

const useCurrentUser = () => {
    const { data: user } = useSuspenseQuery({
        queryKey: userQueryKeys.current(),
        queryFn: () => fetchUser(),
        select: data => {
            const { data: user, ...rest } = data;
            const isPhoneVerified = !!user.phone_verified_at;
            const requiresVerification = !!user.requires_verification;
            const isEmailVerified = !!user.email_verified_at;
            const isCompletelyVerified = isPhoneVerified && isEmailVerified;

            return {
                id: user.id,
                uid: user.uid,
                firstName: user.first_name,
                lastName: user.last_name,
                email: user.email,
                phone: user.phone,
                ldHash: rest.launchdarkly_hash,
                segment_object: rest.segment_object,
                intercom_hash: rest.intercom_hash,
                hasSmsCode: user.has_sms_code,
                picture: user.picture,
                created_at: user.created_at,
                isPhoneVerified,
                isEmailVerified,
                isCompletelyVerified,
                requiresVerification,
                growSurfHash: rest.growsurf_hash,
            };
        },
        staleTime: Infinity,
        retry: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        Sentry.setUser(user);
    }, [user]);

    return user;
};

export { useCurrentUser };
export type { TransformedUser };
